import React from 'react';
import styled, { keyframes } from 'styled-components';
import { SharedLayoutUltimate } from '../components/layout/SharedLayoutUltimate';
import { theme } from '../styles/theme';
import { useCalendlyPopup } from '../hooks/useCalendlyPopup';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -1000px 0; }
  100% { background-position: 1000px 0; }
`;

const PageWrapper = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #1a2332 0%, #2c3e50 100%);
  padding-top: 120px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding-top: 100px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding-top: 80px;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 40px 80px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 0 30px 60px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 0 20px 40px;
  }
`;

const PageTitle = styled.h1`
  font-size: 48px;
  font-weight: 900;
  color: white;
  text-align: center;
  margin-bottom: 60px;
  animation: ${fadeIn} 0.8s ease-out;
  text-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -20px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, transparent, #ffd700, transparent);
    border-radius: 2px;
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 36px;
    margin-bottom: 40px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 28px;
    margin-bottom: 30px;
  }
`;

const SectionsWrapper = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-top: 60px;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
    gap: 30px;
    margin-top: 40px;
  }
`;

const Section = styled.div<{ $delay?: string }>`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%);
  border: 1px solid rgba(255, 215, 0, 0.2);
  border-radius: 20px;
  padding: 40px;
  position: relative;
  overflow: hidden;
  animation: ${fadeIn} 1s ease-out;
  animation-fill-mode: both;
  animation-delay: ${props => props.$delay || '0s'};
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, transparent, #ffd700, transparent);
    animation: ${shimmer} 10s infinite linear;
  }
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
    border-color: rgba(255, 215, 0, 0.4);
  }
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    padding: 30px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 25px;
    border-radius: 15px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 32px;
  font-weight: 800;
  color: #ffd700;
  margin-bottom: 25px;
  text-align: center;
  text-shadow: 0 2px 10px rgba(255, 215, 0, 0.3);
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 28px;
    margin-bottom: 20px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 24px;
  }
`;

const SectionDescription = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.85);
  margin-bottom: 30px;
  text-align: center;
  
  @media (max-width: ${theme.breakpoints.tablet}) {
    font-size: 15px;
    line-height: 1.7;
    margin-bottom: 25px;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    font-size: 14px;
    line-height: 1.6;
    text-align: left;
  }
`;

const Highlight = styled.div`
  background: linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(255, 215, 0, 0.05) 100%);
  border-left: 4px solid #ffd700;
  padding: 20px;
  margin: 25px 0;
  border-radius: 8px;
  
  h3 {
    font-size: 20px;
    font-weight: 700;
    color: #ffd700;
    margin-bottom: 10px;
  }
  
  p {
    font-size: 15px;
    line-height: 1.7;
    color: rgba(255, 255, 255, 0.8);
    margin: 0;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 15px;
    
    h3 {
      font-size: 18px;
    }
    
    p {
      font-size: 14px;
    }
  }
`;

const CTAButton = styled.button<{ $primary?: boolean }>`
  background: ${props => props.$primary ? 
    'linear-gradient(135deg, #ffd700 0%, #ffa500 100%)' : 
    'linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 215, 0, 0.1) 100%)'};
  color: ${props => props.$primary ? '#1a2332' : '#ffd700'};
  border: ${props => props.$primary ? 'none' : '2px solid #ffd700'};
  padding: 16px 32px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 1px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: block;
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%;
    left: 50%;
    width: 0;
    height: 0;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.3);
    transform: translate(-50%, -50%);
    transition: width 0.5s, height 0.5s;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(255, 215, 0, 0.3);
    
    &::before {
      width: 300px;
      height: 300px;
    }
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    padding: 14px 28px;
    font-size: 14px;
  }
`;

const Schedule: React.FC = () => {
  const { openCalendly } = useCalendlyPopup();

  const handleOwnerConsultation = () => {
    openCalendly();
  };

  const handleAdvisorConsultation = () => {
    openCalendly();
  };

  return (
    <SharedLayoutUltimate>
      <PageWrapper>
        <Container>
          <PageTitle>Schedule Your Free Consultation</PageTitle>
          
          <SectionsWrapper>
            <Section $delay="0.2s">
              <SectionTitle>Annuity Owners, or Buyers</SectionTitle>
              <SectionDescription>
                Book your free "All About You" consultation. Tell us what a five-star win looks like 
                for you—what result would make you absolutely thrilled with our work together. 
                We can't promise to deliver every dream scenario, but we do promise this: if you 
                share your goals with us, we'll share exactly how we'd pursue them as if it were 
                our own money—complete with strategies, products, and a clear action plan.
              </SectionDescription>
              <CTAButton $primary={true} onClick={handleOwnerConsultation}>
                Book Free Consultation
              </CTAButton>
            </Section>

            <Section $delay="0.4s">
              <SectionTitle>Annuity Advisors</SectionTitle>
              <SectionDescription>
                Book a free "All About You" consultation to show us how we can be pivotal in your 
                success—or an "All About Your Client" consultation if you need help setting clear 
                expectations and delivering results on an advanced case. Either way, we'll ask the 
                right questions, listen closely, and deliver the strategies we'd use ourselves—so 
                both you and your clients walk away happy.
              </SectionDescription>
              
              <Highlight>
                <h3>Grow your practice the right way.</h3>
                <p>
                  Recommend annuities based on real, dependable historical data—so you win more 
                  often, earn repeat business, get referrals, and keep clients truly satisfied. 
                  We're your advocate, providing research without distortion so you and your 
                  clients can make confident, responsible choices.
                </p>
              </Highlight>
              
              <CTAButton onClick={handleAdvisorConsultation}>
                Schedule Advisor Consultation
              </CTAButton>
            </Section>
          </SectionsWrapper>
        </Container>
      </PageWrapper>
    </SharedLayoutUltimate>
  );
};

export default Schedule;